import React, { useEffect } from 'react';
import { Box, Link, Typography, Paper, useTheme, Chip, Fade } from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import GavelIcon from '@mui/icons-material/Gavel';
import Footer from './Footer';
import AOS from 'aos';
import 'aos/dist/aos.css';

const PrivacyPolicy = () => {
  const theme = useTheme();

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: 'ease-out-cubic',
      disable: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    });
  }, []);

  const sectionStyle = {
    mb: 4,
    p: 3,
    borderRadius: '16px',
    bgcolor: '#f8fffe',
    border: '1px solid rgba(0, 121, 107, 0.08)',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 8px 24px -8px rgba(0, 77, 64, 0.1)',
      transform: 'translateY(-2px)',
    },
  };

  const headingStyle = {
    fontWeight: 700,
    mb: 2,
    color: '#00796b',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    '&::before': {
      content: '""',
      width: 4,
      height: 24,
      borderRadius: 2,
      background: 'linear-gradient(180deg, #00796b 0%, #004d40 100%)',
    },
  };

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh', width: '100%' }}>
      {/* Privacy Policy Section */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          px: { xs: 2, md: 4 },
          background: 'linear-gradient(135deg, #e0f7f4 0%, #b2dfdb 50%, #80cbc4 100%)',
          backgroundSize: '400% 400%',
          animation: 'gradient-shift 15s ease infinite',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Animated Background */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            right: '5%',
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(0, 121, 107, 0.1)',
            filter: 'blur(60px)',
            animation: 'float 8s ease-in-out infinite',
          }}
        />

        <Fade in timeout={600}>
          <Paper
            elevation={0}
            sx={{
              maxWidth: 900,
              width: '100%',
              mx: 'auto',
              p: { xs: 4, md: 6 },
              borderRadius: '28px',
              boxShadow: '0 20px 60px -20px rgba(0, 77, 64, 0.2)',
              bgcolor: 'white',
              border: '1px solid rgba(0, 121, 107, 0.1)',
              position: 'relative',
            }}
            data-aos="fade-up"
          >
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <Chip
                icon={<SecurityIcon sx={{ color: 'white !important' }} />}
                label="Legal Document"
                sx={{
                  background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
                  color: 'white',
                  fontWeight: 600,
                  px: 2,
                  py: 2.5,
                  borderRadius: '50px',
                  mb: 3,
                  boxShadow: '0 4px 20px -4px rgba(0, 121, 107, 0.4)',
                  '& .MuiChip-icon': { color: 'white' },
                }}
              />
              <Typography
                variant="h3"
                sx={{ 
                  fontWeight: 800, 
                  color: '#004d40', 
                  mb: 2,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                }}
              >
                Privacy Policy
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Effective Date: 31 July 2025
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Last Updated: 15 August 2025
                </Typography>
              </Box>
            </Box>

            <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8, color: theme.palette.text.secondary }}>
              This Privacy Policy ("Policy") is published in accordance with the Information Technology Act, 2000 and the rules made thereunder, including the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011. This Policy governs the manner in which Bixbite Innovations Private Limited ("Bixbite," "we," "our," or "us"), operating the websites platform.bixbite.ai and bixbite.ai ("Platform"), collects, uses, maintains, and discloses information collected from users ("User," "you," or "your") of the Websites and our AI-powered tools and services.
            </Typography>

            {[
              {
                title: "1. Definitions",
                content: (
                  <>
                    <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.8 }}>
                      <strong>Personal Data:</strong> Any information that identifies or can be used to identify an individual.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.8 }}>
                      <strong>Cookies:</strong> Small text files stored on your device by a web browser. In our case, we only use cookies to store a session ID for login/session continuity. We do not use cookies for tracking or analytics.
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                      <strong>AI Conversations:</strong> Messages, prompts, and responses you exchange with our AI tools.
                    </Typography>
                  </>
                ),
              },
              {
                title: "2. Scope",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    • Visitors to our company website (platform.bixbite.ai)<br />
                    • Registered users of our AI tool platform (bixbite.ai)<br />
                    • Customers purchasing credits through our integrated payment gateway<br />
                    • Any other interaction you have with Bixbite via email, chat, or other communication channels
                  </Typography>
                ),
              },
              {
                title: "3. Information We Collect",
                content: (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#004d40' }}>A. Information You Provide</Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
                      • Account Information: Name, email address, password, profile details<br />
                      • Billing Information: Payment card details, billing address, and transaction history (processed securely through third-party payment providers)<br />
                      • AI Conversations: Messages, prompts, and content you enter into our platform<br />
                      • Support Requests: Any information you share with our support team
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#004d40' }}>B. Information We Collect Automatically</Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
                      • Usage Data: Pages visited, features used, time spent, interaction logs<br />
                      • Device & Browser Information: IP address, device type, operating system, browser type, and version<br />
                      • Cookies & Tracking Data: We only store a session ID in browser cookies for authentication purposes. No tracking or analytics cookies are used.
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#004d40' }}>C. Information from Third Parties</Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                      • Payment providers (for payment confirmation)<br />
                      • Authentication providers (if you use Google, GitHub, or other SSO login)
                    </Typography>
                  </>
                ),
              },
              {
                title: "4. How We Use Your Information",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    • To create and manage your account<br />
                    • To process payments and deliver purchased credits<br />
                    • To enable and improve AI-based services<br />
                    • To monitor and enhance platform performance<br />
                    • To communicate updates, alerts, and support messages<br />
                    • To comply with legal obligations<br />
                    • To detect and prevent fraud, abuse, or security threats
                  </Typography>
                ),
              },
              {
                title: "5. AI Conversations & Data Usage",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    When you use bixbite.ai, your AI chat inputs and outputs may be stored to generate accurate responses, improve service performance, and investigate misuse or technical issues. We do not train our AI models on user data. No anonymisation is applied, and users cannot opt out of this limited storage, as it is essential for service functionality.
                  </Typography>
                ),
              },
              {
                title: "6. Payment Information",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    We use secure third-party payment processors to handle all transactions. We do not store your full credit/debit card details on our servers. Payment providers comply with PCI-DSS standards. Transaction data (amount, date, order ID) is stored for accounting and legal purposes.
                  </Typography>
                ),
              },
              {
                title: "7. Legal Basis for Processing (GDPR)",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    If you are in the European Economic Area (EEA), we process your data under these lawful bases: Consent (when you accept cookies, create an account, or subscribe to emails), Contract performance (when you purchase credits or use our tool), Legal obligations (tax, accounting, fraud prevention), and Legitimate interests (service improvement, analytics).
                  </Typography>
                ),
              },
              {
                title: "8. How We Share Your Information",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    We may share your information with service providers such as payment processors, hosting providers, and analytics tools; legal authorities if required by law, regulation, or court order; and during business transfers in case of a merger, acquisition, or sale of assets. We do not sell your data to advertisers or third parties.
                  </Typography>
                ),
              },
              {
                title: "9. International Data Transfers",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    Your data may be processed in countries outside your residence. In such cases, we ensure adequate protection measures, including standard contractual clauses where applicable.
                  </Typography>
                ),
              },
              {
                title: "10. Data Retention",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    • Account Data: Stored until you delete your account<br />
                    • Payment Data: Retained for at least 7 years for tax and legal compliance<br />
                    • AI Conversations: May be stored temporarily (up to 90 days) for service improvement, after which they are deleted
                  </Typography>
                ),
              },
              {
                title: "11. Your Rights",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    Depending on your jurisdiction (e.g., GDPR, CCPA), you may have the right to access your personal data, correct or update your data, request deletion of your data, withdraw consent for processing, object to automated processing or profiling, and request a copy of your data (data portability). To exercise these rights, email <Link href="mailto:contact@bixbite.ai" underline="always" sx={{ color: '#00796b', fontWeight: 600 }}>contact@bixbite.ai</Link>.
                  </Typography>
                ),
              },
              {
                title: "12. Security Measures",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    We use encryption (HTTPS, SSL/TLS), firewalls, and secure storage to protect your information. However, no method of transmission or storage is 100% secure, and we cannot guarantee absolute security.
                  </Typography>
                ),
              },
              {
                title: "13. Changes to This Privacy Policy",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    We may update this policy from time to time. The updated version will be posted on this page with a new Updated Date.
                  </Typography>
                ),
              },
              {
                title: "14. Contact Us",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    If you have any questions or concerns about this Privacy Policy, contact us at:<br />
                    Email: <Link href="mailto:contact@bixbite.ai" underline="always" sx={{ color: '#00796b', fontWeight: 600 }}>contact@bixbite.ai</Link><br />
                    Website: <Link href="https://platform.bixbite.ai/" target="_blank" rel="noopener" underline="always" sx={{ color: '#00796b', fontWeight: 600 }}>https://platform.bixbite.ai/</Link>
                  </Typography>
                ),
              },
            ].map((section, index) => (
              <Box key={index} component="section" sx={sectionStyle}>
                <Typography variant="h5" sx={headingStyle}>{section.title}</Typography>
                {section.content}
              </Box>
            ))}
          </Paper>
        </Fade>
      </Box>

      {/* Terms and Conditions Section */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          px: { xs: 2, md: 4 },
          background: 'linear-gradient(135deg, #b2dfdb 0%, #80cbc4 50%, #4db6ac 100%)',
          backgroundSize: '400% 400%',
          animation: 'gradient-shift 15s ease infinite',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Animated Background */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '10%',
            left: '5%',
            width: 250,
            height: 250,
            borderRadius: '50%',
            background: 'rgba(0, 77, 64, 0.08)',
            filter: 'blur(80px)',
            animation: 'floatReverse 10s ease-in-out infinite',
          }}
        />

        <Fade in timeout={800}>
          <Paper
            elevation={0}
            sx={{
              maxWidth: 900,
              width: '100%',
              mx: 'auto',
              p: { xs: 4, md: 6 },
              borderRadius: '28px',
              boxShadow: '0 20px 60px -20px rgba(0, 77, 64, 0.2)',
              bgcolor: 'white',
              border: '1px solid rgba(0, 121, 107, 0.1)',
              position: 'relative',
            }}
            data-aos="fade-up"
          >
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <Chip
                icon={<GavelIcon sx={{ color: 'white !important' }} />}
                label="Legal Document"
                sx={{
                  background: 'linear-gradient(135deg, #004d40 0%, #00251a 100%)',
                  color: 'white',
                  fontWeight: 600,
                  px: 2,
                  py: 2.5,
                  borderRadius: '50px',
                  mb: 3,
                  boxShadow: '0 4px 20px -4px rgba(0, 77, 64, 0.4)',
                  '& .MuiChip-icon': { color: 'white' },
                }}
              />
              <Typography
                variant="h3"
                sx={{ 
                  fontWeight: 800, 
                  color: '#004d40', 
                  mb: 2,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                }}
              >
                Terms and Conditions
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Effective Date: 31 July 2025
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Last Updated: 15 August 2025
                </Typography>
              </Box>
            </Box>

            <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8, color: theme.palette.text.secondary }}>
              These Terms and Conditions ("Terms") govern your access to and use of the services provided by Bixbite Innovations Private Limited ("Bixbite," "we," "our," "us") through our website platform.bixbite.ai and our tool bixbite.ai (collectively, the "Platform"). By creating an account or using our services, you agree to these Terms. If you do not agree, you must not use the Platform.
            </Typography>

            {[
              {
                title: "1. Acceptance of Terms",
                text: "By creating an account, you acknowledge that you have read, understood, and agree to be bound by these Terms. You also agree that these Terms may be updated or modified from time to time, and your continued use of the Platform constitutes acceptance of the updated Terms.",
              },
              {
                title: "2. Services",
                text: "Bixbite provides AI-powered tools and services that enable users to interact with artificial intelligence models. The responses generated are based on your inputs, conversation history, and the AI model's capabilities.",
              },
              {
                title: "3. Accuracy of Information & AI Limitations",
                text: "Our AI models may make mistakes, hallucinate, or provide inaccurate or incomplete information. Responses should not be considered professional, legal, financial, medical, or any other form of expert advice. You are solely responsible for evaluating and verifying the information provided by the Platform before using it.",
              },
              {
                title: "4. User Accounts",
                text: "You must create an account to access certain features of the Platform. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.",
              },
              {
                title: "5. Payments & Credits",
                text: "We use a secure third-party payment gateway for transactions. When you purchase credits, they can only be used within our Platform and are non-refundable. All prices are displayed in your local currency where possible, but charges may be processed in INR or USD depending on payment gateway policies. You are responsible for any applicable taxes or charges imposed by your bank or payment provider.",
              },
              {
                title: "6. No Refund Policy",
                text: "All purchases, including credits, subscriptions, and other paid features, are final and non-refundable, except where required by law.",
              },
              {
                title: "7. International Users",
                text: "We provide services to users worldwide. However, you are responsible for ensuring that your use of the Platform complies with the laws and regulations of your country.",
              },
              {
                title: "8. Data Use & Privacy",
                text: "We do not train our AI models on user messages. We do not anonymise user data for training purposes because no training occurs with user data. Users cannot opt out of training since no training takes place. We store only a session ID in browser cookies for functional purposes and do not store tracking or analytics cookies. We do not transfer your personal data to third parties except as required to provide our services. Our Privacy Policy governs the collection and use of your information.",
              },
              {
                title: "9. Intellectual Property & Ownership of AI-Generated Content",
                text: "All content, features, and functionality of the Platform, including but not limited to software, text, graphics, and logos, are owned by Bixbite or its licensors and are protected by copyright, trademark, and other intellectual property laws. For AI-generated content created through the Platform, ownership is shared between you and Bixbite. Both parties have the right to use, reproduce, and modify such content, subject to applicable laws and these Terms.",
              },
              {
                title: "10. Prohibited Activities",
                text: "You agree not to: Use the Platform for any illegal or unauthorised purpose; Attempt to reverse-engineer, copy, or replicate our AI models or services; Submit harmful, malicious, or inappropriate content; Violate any applicable laws in your jurisdiction.",
              },
              {
                title: "11. Service Availability",
                text: "We strive to keep the Platform available at all times but do not guarantee uninterrupted service. We reserve the right to modify, suspend, or discontinue the Platform at any time without notice.",
              },
              {
                title: "12. Termination",
                text: "We may suspend or terminate your access to the Platform without notice if you violate these Terms or engage in harmful behaviour.",
              },
              {
                title: "13. Limitation of Liability",
                text: "Bixbite is not liable for any direct, indirect, incidental, consequential, or punitive damages arising from your use of the Platform, including reliance on AI-generated content.",
              },
              {
                title: "14. Governing Law",
                text: "These Terms are governed by and construed in accordance with the laws of India. Any disputes will be subject to the exclusive jurisdiction of the courts in Hyderabad, India.",
              },
              {
                title: "15. Force Majeure",
                text: "We are not liable for any delay or failure to perform resulting from causes beyond our reasonable control, including but not limited to natural disasters, internet outages, strikes, government actions, or equipment failures.",
              },
              {
                title: "16. Dispute Resolution",
                text: "In the event of a dispute, both you and Bixbite agree to first attempt resolution through informal discussions. If unresolved, disputes will be settled through arbitration or mediation before resorting to legal action in the courts of Hyderabad, India.",
              },
              {
                title: "17. Contact Us",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    If you have any questions about these Terms, contact us at:<br />
                    Email: <Link href="mailto:contact@bixbite.ai" underline="always" sx={{ color: '#00796b', fontWeight: 600 }}>contact@bixbite.ai</Link><br />
                    Website: <Link href="https://platform.bixbite.ai/" target="_blank" rel="noopener" underline="always" sx={{ color: '#00796b', fontWeight: 600 }}>https://platform.bixbite.ai/</Link>
                  </Typography>
                ),
              },
            ].map((section, index) => (
              <Box key={index} component="section" sx={sectionStyle}>
                <Typography variant="h5" sx={headingStyle}>{section.title}</Typography>
                {section.content || (
                  <Typography variant="body1" sx={{ lineHeight: 1.8, color: theme.palette.text.secondary }}>
                    {section.text}
                  </Typography>
                )}
              </Box>
            ))}
          </Paper>
        </Fade>
      </Box>

      <Footer />
    </Box>
  );
};

export default PrivacyPolicy;
